import { Storage } from '@capacitor/storage'

class DomainsList {
  constructor ({ env, remoteListUri, storageKey, fallbackDomainList }) {
    this._env = env
    this._remoteListUri = remoteListUri
    this._storageKey = storageKey
    this._fallbackDomainList = fallbackDomainList

    this._stagingListKey = 'staging'
    this._productionListKey = 'mirror'

    this._domainList = null
  }

  async init () {
    const res = await Storage.get({ key: this._storageKey })
    if (res.value) {
      this._domainList = JSON.parse(res.value)
    }

    try {
      if (!res.value) {
        this._domainList = await this._getRemoteDomainList()
        await Storage.set({
          key: this._storageKey,
          value: JSON.stringify(this._domainList)
        })
      }
    } catch (e) {
      this._domainList = this._fallbackDomainList
    }

    this._productionListKey = await this._getActiveKey()
    return this
  }

  async _getRemoteDomainList () {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 1500)
    return fetch(this._remoteListUri, { signal: controller.signal }).then(res => {
      clearTimeout(timeoutId)
      return res.json()
    })
  }

  /**
   * @param {{key: string, uri: string}[]} verifyObjects
   * @return {Promise<string>} key
   * @private
   */
  async _getFirstAvailableKey (verifyObjects) {
    let timeoutId
    let errorCount = 0
    return new Promise((resolve, reject) => {
      const xhr = []
      verifyObjects.forEach((verifyObj, i) => {
        xhr[i] = new XMLHttpRequest()
        xhr[i].open('GET', verifyObj.uri)
        xhr[i].send()
        xhr[i].onload = function () {
          if (xhr[i].status === 200) {
            resolve(verifyObj.key)
            clearTimeout(timeoutId)
            xhr.forEach((xhr2, i2) => {
              if (i2 !== i) {
                xhr2.abort()
              }
            })
          }

          if (xhr[i].status !== 200) {
            errorCount = errorCount + 1
            if (errorCount === verifyObjects.length) {
              clearTimeout(timeoutId)
              reject(new Error('allDomainsUnavailable'))
            }
          }
        }
      })

      timeoutId = setTimeout(() => {
        reject(new Error('allDomainsUnavailable'))
      }, 5000)
    })
  }

  /**
   * @param {{key: string, uri: string}[]} verifyObjects
   * @param {string} fallbackString
   * @return {Promise<string>}
   * @private
   */
  async _getFirstAvailableKeyWithFallback (verifyObjects, fallbackString) {
    try {
      return await this._getFirstAvailableKey([verifyObjects[1]])
    } catch (e) {
      try {
        await this._refreshDomainList()
        return this._getFirstAvailableKey([verifyObjects[0]])
      } catch (e) {
        return fallbackString
      }
    }
  }

  /**
   * @description Graphql backend path
   * @return {Promise<string>}
   */
  async _getActiveKey () {
    if (this._env === 'production') {
      return this._getFirstAvailableKeyWithFallback(
        [
          {
            key: 'production',
            uri: `https://${this._domainList.production.merchant_gql}/query`
          },
          {
            key: 'mirror',
            uri: `https://${this._domainList.mirror.merchant_gql}/query`
          }
        ],
        'mirror'
      )
    }

    return 'staging'
  }

  get _keyByEnv () {
    if (this._env === 'production') {
      return this._productionListKey
    }

    return this._stagingListKey
  }

  /**
   * @description JAVA Backend path
   * @return {string}
   */
  get getActiveBackendPath () {
    return `https://${this._domainList[this._keyByEnv].merchant_api}`
  }

  /**
   * @description S3 Files base uri
   * @return {string}
   */
  get getActiveStaticPath () {
    return `https://${this._domainList[this._keyByEnv].switips_files}`
  }

  /**
   * @description Bill path
   * @return {string}
   */
  get getActiveBillPath () {
    if (!this._domainList[this._keyByEnv].merchant_bill) {
      return 'https://bill.merchantstore.services'
    }
    return `https://${this._domainList[this._keyByEnv].merchant_bill}`
  }

  get getActiveEndpoint () {
    return `https://${this._domainList[this._keyByEnv].merchant_gql}/query`
  }

  get productionStaticPath () {
    if (this._env !== 'production') {
      return `https://${this._domainList.production.switips_files}`
    }

    return `https://${this._domainList[this._keyByEnv].switips_files}`
  }

  async _refreshDomainList () {
    this._domainList = await this._getRemoteDomainList()
    await Storage.set({
      key: this._storageKey,
      value: JSON.stringify(this._domainList)
    })
  }
}
/**
 * @return {Promise<DomainsList>}
 */
export async function getDomainList () {
  if (!getDomainList._instance) {
    getDomainList._instance = await new DomainsList({
      env: process.env.NODE_ENV,
      storageKey: process.env.VUE_APP_DOMAIN_LIST_STORAGE_KEY || 'domainList',
      remoteListUri: process.env.VUE_APP_REMOTE_DOMAIN_LIST_URI || 'https://swwwp.s3.eu-central-1.amazonaws.com/d.json',
      fallbackDomainList: {
        production: {
          merchant_bill: 'bill.merchantstore.services',
          merchant_gql: 'office-gql.switips.com',
          switips_files: 'files.switips.com',
          merchant_api: 'backend.switips.com'
        },
        staging: {
          merchant_bill: process.env.VUE_APP_DEFAULT_MERCHANT_BILL || 'bill.merchantstore.services',
          merchant_gql: process.env.VUE_APP_DEFAULT_GRAPHQL_HTTP || 'staging-office-gql.switips.com',
          switips_files: process.env.VUE_APP_DEFAULT_FILES_PATH || 'staging-files.switips.com',
          merchant_api: process.env.VUE_APP_DEFAULT_BACKEND_PATH || 'staging-backend.switips.com'
        },
        mirror: {
          merchant_bill: process.env.VUE_APP_DEFAULT_MERCHANT_BILL || 'bill.merchantstore.services',
          merchant_gql: process.env.VUE_APP_DEFAULT_GRAPHQL_HTTP || 'merchant-gql.swwwp.com',
          switips_files: process.env.VUE_APP_DEFAULT_FILES_PATH || 'files.swwwp.com',
          merchant_api: process.env.VUE_APP_DEFAULT_BACKEND_PATH || 'merchant-api.swwwp.com'
        }
      }
    }).init()
  }

  return getDomainList._instance
}
